import { compareDates, getDateFromSquidex, unserializeDate } from '@/services/dates';
import { isMatchedValue } from '@/services/forms';

export function accumulateValues( questions: any[], cb: Function, prefix?: string ) {
	questions.forEach( ( question: any ) => {
		cb({
			...question,
			prefix
		});

		const additional = question.additionalQuestions || question.additionalquestions || question.additionalYes || question.additionalNo || question.additionalNa || question.questions;

		if ( additional ) {
			accumulateValues( additional, cb, prefix );
		}

		if ( question.fieldType === 'TableEmbeddedForm' ) {
			question.embeddedForms.forEach( ( form: any, index: number ) => {
				// [TODO]: Find a more reliable way to do this.
				const newPrefix = [ prefix, `${ question.systemId }[${ index }]`, form.formCode === 'newLocation' ? form.formCode : '' ].filter( a => a ).join( '.' );

				if ( form.questions ) {
					if ( newPrefix ) {
						const mappedForm = form.questions.map( ( question: any ) => { 
							return {
								...question,
								prefix: newPrefix
							};
						});
	
						accumulateValues( mappedForm, cb, newPrefix );
					} else {
						accumulateValues( form.questions, cb, prefix );
					}
				}
			});
		}

		if ( question.fieldType === 'Select' ) {
			accumulateValues( question.options.map( ( option: any ) => {
				return {
					...option,
					fieldType: 'SelectOption',
					parentLabel: question.label
				};
			}), cb, prefix );
		}

		if ( question.fieldType === 'Radio' ) {
			question.radioButtons.forEach( ( button: any ) => {
				if ( button.additionalQuestions ) {
					accumulateValues( button.additionalQuestions, cb, prefix );
				}
			});
		}
	});
}

export function mapValues( questions: any[], cb: Function ) {
	return questions.map( ( question: any ) => {
		let copiedQuestion = { ...question };

		cb( copiedQuestion );

		if ( copiedQuestion.questions ) {
			copiedQuestion.questions = mapValues( copiedQuestion.questions, cb );
		}

		if ( copiedQuestion.additionalQuestions ) {
			copiedQuestion.additionalQuestions = mapValues( copiedQuestion.additionalQuestions, cb );
		}

		if ( copiedQuestion.additionalquestions ) {
			copiedQuestion.additionalquestions = mapValues( copiedQuestion.additionalquestions, cb );
		}

		if ( copiedQuestion.additionalYes ) {
			copiedQuestion.additionalYes = mapValues( copiedQuestion.additionalYes, cb );
		}

		if ( copiedQuestion.additionalNo ) {
			copiedQuestion.additionalNo = mapValues( copiedQuestion.additionalNo, cb );
		}

		if ( copiedQuestion.additionalNa ) {
			copiedQuestion.additionalNa = mapValues( copiedQuestion.additionalNa, cb );
		}

		if ( copiedQuestion.fieldType === 'Select' ) {
			copiedQuestion.options = copiedQuestion.options.map( ( option: any ) => {
				return {
					...option,
					additionalQuestions: mapValues( option.additionalQuestions, cb )
				};
			});
		}

		if ( copiedQuestion.fieldType === 'Radio' ) {
			copiedQuestion.radioButtons = copiedQuestion.radioButtons.map( ( button: any ) => {
				return {
					...button,
					additionalQuestions: mapValues( button.additionalQuestions, cb )
				};
			});
		}

		return copiedQuestion;
	});
}

export function toFlatPropertyMap( obj: object, keySeparator = '.' ) {
	function flattenRecursive( obj: object, parentProperty?: string, propertyMap: Record<string, unknown> = {}, isArray = false ) {
		for( const [ key, value ] of Object.entries( obj ) ) {
			const adjustedKey = isArray ? `[${ key }]` : key;
			const property = parentProperty ? `${ parentProperty }${ isArray ? '' : keySeparator }${ adjustedKey }` : adjustedKey;

			// for our purposes, we need to include the structured version
			// of array type values. Long story.
			if ( Array.isArray( value ) ) {
				propertyMap[ property ] = value;
			}

			if ( value && typeof value === 'object' ) {
				flattenRecursive( value, property, propertyMap, Array.isArray( value ) );
			} else {
				propertyMap[ property ] = value;
			}
		}
		
		return propertyMap;
	};
	
	return flattenRecursive( obj );
}

function shouldShowAdditionalQuestions( fieldType: string, value: string | string[], systemId: string, getValues: any ) {
	const typesToPassThrough = [ 'Section', 'InputGroup', 'QuestionTable', 'Table' ];

	if ( typesToPassThrough.includes( fieldType ) ) {
		return true;
	}

	const expectedQuestionValue = value;
	const actualQuestionValue = getValues( systemId );

	if ( Array.isArray( actualQuestionValue ) ) {
		return actualQuestionValue.includes( expectedQuestionValue );
	} else {
		return actualQuestionValue === expectedQuestionValue;
	}
}

export function accumulateVisibleValues( questions: any[], cppData: any, getValues: any, cb: Function, prefix?: string, parentForm?: string ) {
	for ( const question of questions ) {
		const validators = question.validation || [];

		// check visibility
		let isVisibleDate = true;
		let isVisibleComparison = true;

		// date validator
		const dateValidators = validators.filter( ( validator: any ) => validator.fieldType === 'EffectiveDateComparison' );

		if ( dateValidators.length > 0 ) {
			isVisibleDate = dateValidators.every( ( validator: any ) => {
				return compareDates( getDateFromSquidex( validator.date ), unserializeDate( cppData.quoteEffectiveDate ), validator.timePeriod );
			});
		}

		// value comparison validator
		const comparisonValidators = validators.filter( ( validator: any ) => validator.fieldType === 'ValueComparison' );

		if ( comparisonValidators.length > 0 ) {
			isVisibleComparison = comparisonValidators.map( ( validator: any ) => {
				return validator.comparisons[ validator.requireEvery ? 'every' : 'some' ]( ( comparison: any ) => {
					return isMatchedValue( getValues( comparison.systemId ), comparison.value );
				});
			}).some( ( a: any ) => a );
		}

		if ( !isVisibleDate || !isVisibleComparison ) {
			continue;
		}

		const questionCopy = Object.assign( question );

		if ( questionCopy.systemId ) {
			// we need to check to see if the subforms incorrectly include index prefixes
			let splitId = questionCopy.systemId.split( '.' );
			const reg = /\[([^\]]+)\]/;

			const prefixWithIndex = splitId.findIndex( ( segment: string ) => {
				const matched = reg.exec( segment );

				return matched;
			});

			if ( prefixWithIndex >= 0 ) {
				splitId[ 0 ] = prefix;

				questionCopy.systemId = splitId.join( '.' );
			} else {
				questionCopy.systemId = [ prefix, question.systemId ].filter( a => a ).join( '.' );
			}
		}

		cb({
			...questionCopy,
			prefix,
			parentForm
		});

		const additional = question.additionalQuestions || question.additionalquestions || question.additionalYes || question.additionalNo || question.additionalNa || question.questions;

		if ( additional?.length > 0 ) {
			// check to see if additional values should be considered
			let specificValue;

			if ( question.additionalYes?.length > 0 ) {
				specificValue = 'yes';
			}

			if ( question.additionalNo?.length > 0 ) {
				specificValue = 'no';
			}

			if ( question.additionalNa?.length > 0 ) {
				specificValue = 'na';
			}

			const showAdditionalQuestions = shouldShowAdditionalQuestions( question.fieldType, specificValue || question.value, question.systemId, getValues );

			if ( question.questions || showAdditionalQuestions ) {
				accumulateVisibleValues( additional, cppData, getValues, cb, prefix, parentForm );
			}
		}

		if ( question.fieldType === 'TableEmbeddedForm' ) {
			const path = [ question.prefix, question.systemId ].filter( a => a ).join( '.' );
			const existingRecords = getValues( path ) || [];

			for ( const [ existingIndex, existingRecord ] of existingRecords.entries() ) {
				const matchedForm = question.embeddedForms.find( ( embeddedForm: any ) => {
					return embeddedForm.id === existingRecord.formGuid;
				});

				// const newPrefix = [ prefix, `${ question.systemId }[${ existingIndex }]`, matchedForm.formCode === 'newLocation' ? matchedForm.formCode : '' ].filter( a => a ).join( '.' );
				const newPrefix = [ prefix, `${ question.systemId }[${ existingIndex }]` ].filter( a => a ).join( '.' );
				
				if ( matchedForm.questions ) {
					if ( newPrefix ) {
						const mappedForm = matchedForm.questions.map( ( question: any ) => { 
							return {
								...question,
								prefix: newPrefix
							};
						});

						accumulateVisibleValues( mappedForm, cppData, getValues, cb, newPrefix, [ parentForm, matchedForm.title ].filter( a => a ).join( ' - ' ) );
					} else {
						accumulateVisibleValues( matchedForm.questions, cppData, getValues, cb, prefix, [ parentForm, matchedForm.title ].filter( a => a ).join( ' - ' ) );
					}
				}
			}
		}

		if ( question.fieldType === 'MultiItem' ) {
			const existingRecords = getValues( [ question.prefix, question.systemId ].filter( a => a ).join( '.' ) ) || [];

			for ( const [ existingIndex, existingRecord ] of existingRecords.entries() ) {
				if ( existingRecord ) {
					const newPrefix = `${ question.systemId }[${ existingIndex }]`;
	
					question.templateRow.forEach( ( templateColumn: any ) => {
						const mappedQuestions = templateColumn.columnQuestions.map( ( question: any ) => {
							return {
								...question,
								prefix: newPrefix
							};
						});
	
						accumulateVisibleValues( mappedQuestions, cppData, getValues, cb, newPrefix, parentForm );
					});
				}
			}
		}

		if ( question.fieldType === 'Select' ) {
			accumulateVisibleValues( question.options.map( ( option: any ) => {
				return {
					...option,
					fieldType: 'SelectOption',
					parentLabel: question.label
				};
			}), cppData, getValues, cb, prefix, parentForm );
		}

		if ( question.fieldType === 'Radio' ) {
			question.radioButtons.forEach( ( button: any ) => {
				if ( button.additionalQuestions && shouldShowAdditionalQuestions( question.fieldType, button.value, question.systemId, getValues ) ) {
					accumulateVisibleValues( button.additionalQuestions, cppData, getValues, cb, prefix, parentForm );
				}
			});
		}
	}
}
